import {
	BN,
	QUOTE_PRECISION,
	SPOT_MARKET_WEIGHT_PRECISION,
	SpotBalanceType,
	SpotMarketAccount,
	ZERO,
	getTokenAmount,
	getTokenValue,
} from '@drift-labs/sdk';

export type IAWDataPoint = {
	x: number;
	y: number;
};

// Generate chart data for global deposit scaling
export function generateGlobalChartData(
	spotMarketAccount: SpotMarketAccount | undefined,
	oraclePrice: BN | undefined,
	numPoints = 100
): IAWDataPoint[] {
	const data: IAWDataPoint[] = [];

	if (!spotMarketAccount || !oraclePrice) {
		return data;
	}

	const deposits = getTokenAmount(
		spotMarketAccount.depositBalance,
		spotMarketAccount,
		SpotBalanceType.DEPOSIT
	);
	const currentDepositsValue = getTokenValue(
		deposits,
		spotMarketAccount.decimals,
		{
			price: oraclePrice,
		}
	);

	const initialAssetWeight = new BN(spotMarketAccount.initialAssetWeight);
	const scaleThreshold = spotMarketAccount.scaleInitialAssetWeightStart;

	// Adjust range to always show the inflection point (scaleInitialAssetWeightStart)
	let maxDepositsValue: BN;
	if (scaleThreshold.eq(ZERO)) {
		// No scaling, just show reasonable range around current
		maxDepositsValue = currentDepositsValue.isZero()
			? new BN(1000000000) // $1B default
			: currentDepositsValue.mul(new BN(4));
	} else {
		// Show range that includes the threshold and demonstrates scaling
		// Range: 0 to max(current * 1.5, threshold * 3)
		const thresholdBased = scaleThreshold.mul(new BN(3));
		const currentBased = currentDepositsValue.mul(new BN(15)).div(new BN(10));
		maxDepositsValue = BN.max(thresholdBased, currentBased);
	}

	for (let i = 0; i <= numPoints; i++) {
		const depositsValue = maxDepositsValue
			.mul(new BN(i))
			.div(new BN(numPoints));

		let iaw: BN;
		if (scaleThreshold.eq(ZERO) || depositsValue.lt(scaleThreshold)) {
			iaw = initialAssetWeight;
		} else {
			iaw = initialAssetWeight.mul(scaleThreshold).div(depositsValue);
		}

		const iawPercent =
			iaw.mul(new BN(100)).toNumber() / SPOT_MARKET_WEIGHT_PRECISION.toNumber();

		const depositsUSD = depositsValue.toNumber() / QUOTE_PRECISION.toNumber();

		data.push({
			x: depositsUSD,
			y: iawPercent,
		});
	}

	return data;
}
